import classnames from 'classnames';
import * as React from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../components/button/index';
import SingleFilter from '../../../../../../components/SelectFilters/SingleFilter';
import { SelectDropdown } from '../../../../../../components/Select/SelectDropdown';
import {
  ApprovalStatus,
  GoalType,
  ObjectiveMode,
  ObjectiveWeightMode,
} from '../../../../__generated__/graphql';
import {
  GoalBase,
  GoalsFilterType,
  GoalStatus,
  GoalViewBy,
  isGoalViewBy,
} from '../types';
import { StatusStackedBarChart } from './StatusStackedBarChart';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import { GoalPhase } from './GoalPhase';
import { getViewByOptions } from '../translations';
import { GoalWeightValidationBanner } from '../GoalWeightValidationBanner';
import { ManagerWeightValidationBadge } from '../ManagerWeightValidationBadge';
import { GoalWeightValidation } from '../useGoalWeightValidation';

type Option = {
  label: string;
  value: string;
};

export type GoalFilter = {
  filterType: GoalsFilterType;
  tagId: string | null;
  // for back compatibility, with Development Framework enabled, we need to use currentPeriodId from GoalsContext
  periodId: string | null;
  employeeId: string | null;
  locationId: string | null;
  goalType: GoalType | null;
  categoryId: string | null;
  status: GoalStatus | null;
  approvalStatus: ApprovalStatus | null;
};

type Props = {
  tagsOptions: Option[];
  periodsOptions: Option[];
  employeeOptions: Option[];
  goalTypeOptions: Option[];
  categoryOptions: Option[];
  statusOptions: Option[];
  goals: GoalBase[];
  onClearFilter: () => void;
  onChange: (filter: GoalFilter) => void;
  filter: GoalFilter;
  showEmployeeFilter: boolean;
  viewBy: GoalViewBy;
  onViewByChange: (viewBy: GoalViewBy) => void;
  goalWeightValidation: GoalWeightValidation;
  onActivateInvalidOnly: () => void;
};

const i18prefix = 'performanceReview.goals2.goalsFilter';

export const GoalsFilter: React.FC<Props> = ({
  goals,
  tagsOptions,
  periodsOptions,
  employeeOptions,
  goalTypeOptions,
  categoryOptions,
  statusOptions,
  onClearFilter,
  onChange,
  filter,
  showEmployeeFilter,
  viewBy,
  onViewByChange,
  goalWeightValidation,
  onActivateInvalidOnly,
}) => {
  const { t } = useTranslation();
  const {
    currentPeriod,
    developmentFrameworkEnabled,
    objectiveMode,
    objectiveWeightMode,
    isManagerView,
  } = useGoalsContext();
  const showWeights = objectiveWeightMode !== ObjectiveWeightMode.None;
  const isMbo = objectiveMode === ObjectiveMode.Mbo;

  const categoriesCount =
    categoryOptions.length > 0 ? categoryOptions.length - 1 : 0;

  const buildFilters = () => {
    return (
      <Row className={classnames('align-items-center', 'row-cols-auto', 'g-3')}>
        <Col>
          <span>{t(`${i18prefix}.filterBy`)}</span>
        </Col>
        {developmentFrameworkEnabled && (
          <Col>
            <SingleFilter
              placeholder={t(`${i18prefix}.category`)}
              options={categoryOptions}
              value={filter.categoryId}
              onChange={(value) =>
                onChange({
                  ...filter,
                  categoryId: value,
                })
              }
            />
          </Col>
        )}
        {!isMbo && (
          <Col>
            <SingleFilter
              placeholder={t(`${i18prefix}.status`)}
              options={
                showEmployeeFilter
                  ? [
                      ...statusOptions,
                      {
                        label: t(
                          'performanceReview.goals2.approvalStatus.pendingApproval',
                        ),
                        value: 'PENDING_APPROVAL',
                      },
                    ]
                  : statusOptions
              }
              value={
                filter.approvalStatus === ApprovalStatus.Pending
                  ? 'PENDING_APPROVAL'
                  : filter.status
              }
              onChange={(value) => {
                if (value === 'PENDING_APPROVAL') {
                  onChange({
                    ...filter,
                    status: null,
                    approvalStatus: ApprovalStatus.Pending,
                  });
                } else {
                  onChange({
                    ...filter,
                    status: value as GoalStatus,
                    approvalStatus: null,
                  });
                }
              }}
            />
          </Col>
        )}
        <Col>
          <SingleFilter
            placeholder={t(`${i18prefix}.tags`)}
            options={tagsOptions}
            value={filter.tagId}
            onChange={(value) => {
              return onChange({
                ...filter,
                tagId: value,
              });
            }}
          />
        </Col>
        {!developmentFrameworkEnabled && (
          <Col>
            <SingleFilter
              placeholder={t(`${i18prefix}.period`)}
              options={periodsOptions}
              value={filter.periodId}
              onChange={(value) =>
                onChange({
                  ...filter,
                  periodId: value,
                })
              }
            />
          </Col>
        )}
        {showEmployeeFilter && (
          <Col>
            <SingleFilter
              placeholder={t(`${i18prefix}.employee`)}
              options={employeeOptions}
              isSearchable={true}
              value={filter.employeeId}
              onChange={(value) =>
                onChange({
                  ...filter,
                  employeeId: value,
                })
              }
            />
          </Col>
        )}
        <Col>
          <SingleFilter
            placeholder={t(`${i18prefix}.goalType`)}
            options={goalTypeOptions}
            isSearchable={true}
            value={filter.goalType}
            onChange={(value) =>
              onChange({
                ...filter,
                goalType: value as GoalType,
              })
            }
          />
        </Col>

        <Col>
          <Button
            variant="link"
            onClick={onClearFilter}
            label={t(`${i18prefix}.clearFilters`)}
          />
        </Col>
      </Row>
    );
  };

  const viewByOptions = getViewByOptions(isMbo, isManagerView);

  const buildViewBy = () => {
    return (
      <div
        className={classnames('d-flex', 'align-items-center', 'gap-2', 'mt-1')}>
        <span className="text-nowrap">{t(`${i18prefix}.viewBy`)}</span>
        <SelectDropdown
          className="categoryDropdown"
          placeholder={t(`${i18prefix}.viewBy`)}
          options={viewByOptions}
          value={viewBy}
          onChange={(value) => {
            if (isGoalViewBy(value)) {
              onViewByChange(value);
            }
          }}
          minWidth="10rem"
        />
      </div>
    );
  };

  function getCountText() {
    let result =
      goals.length +
      ' ' +
      t('performanceReview.goals2.goal', {
        count: goals.length,
      });
    if (categoriesCount > 0) {
      const categoriesText =
        categoriesCount +
        ' ' +
        t('performanceReview.goals2.category', {
          count: categoriesCount,
        });
      result += ' • ' + categoriesText;
    }
    return result;
  }

  return (
    <Card>
      <Card.Body className="p-4">
        <Row
          className={classnames(
            'justify-content-md-between',
            'align-content-between',
            'align-items-center',
          )}>
          <Col sm={8}>
            <Row className={classnames('align-items-center', 'g-3')}>
              <Col xs="auto">
                <span className="fw-bold">{getCountText()}</span>
              </Col>
              {currentPeriod?.currentPhase && (
                <Col xs="auto">
                  <GoalPhase phase={currentPeriod.currentPhase} />
                </Col>
              )}
            </Row>
            {!isMbo && (
              <Row className={classnames('py-4')}>
                <Col>
                  <StatusStackedBarChart goals={goals} />
                </Col>
              </Row>
            )}
          </Col>
          <Col sm={4} className="d-flex align-items-start justify-content-end">
            {showWeights &&
              (isManagerView ? (
                <ManagerWeightValidationBadge
                  invalidEmployeeCount={
                    goalWeightValidation.invalidEmployeeCount
                  }
                  onClick={onActivateInvalidOnly}
                />
              ) : (
                <GoalWeightValidationBanner
                  goalWeightValidation={goalWeightValidation}
                />
              ))}
          </Col>
        </Row>
        <Row
          className={classnames(
            'justify-content-md-between',
            'align-content-between',
            'align-items-center',
            'mt-3',
          )}>
          <Col sm={8}>{buildFilters()}</Col>
          <Col sm={4}>
            {developmentFrameworkEnabled && viewByOptions.length > 1 && (
              <div
                className={classnames(
                  'd-flex',
                  'flex-sm-column',
                  'align-items-end',
                )}>
                {buildViewBy()}
              </div>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};
